import { z, type ZodTypeAny } from 'zod';
import type { FieldConfig } from '@/components/blocks/forms';
import {
  companyFormFields,
  companyFormSchema,
} from '@/features/companies/components/companyFormSchema';
import { siteFormFields, siteFormSchema } from '@/features/companies/components/siteFormSchema';
import type { CompanyWithSites } from '@/data/repos/companiesRepo';
import type { RefrigerationUnit, Site } from '@/data/schemas';
import type { EntityFieldDescriptor } from '@/features/reports/components/fields/FieldRenderer';
import type { SupportedEntity } from '@/features/reports/utils/fieldSourceParser';
import { optionalDate, optionalNumber, optionalText, requiredText } from '@/lib/formSchemas';
import { formatItDateInputValue } from '@/utils/date';
import { newId } from '@/utils/ids';
import { normalizeFieldText } from '@/data/fieldPatterns';
import {
  ACCESSIBILITY_OPTIONS,
  EXTRACT_OPERATION_OPTIONS,
  LOCATION_OPTIONS,
  MATERIAL_OPTIONS,
  ORIENTATION_OPTIONS,
  SIGNAGE_OPTIONS,
  VVF_DOCS_OPTIONS,
} from '@/features/companies/screens/RefrigerationUnitDetail/refrigerationUnitFormOptions';

const COMPANY_PERSONNEL_PATHS = new Set([
  'reportRecipients',
  'alarmReference.contact',
  'personnel',
]);

const COMPANY_PATH_MAP: Record<string, string | 'personnel'> = {
  name: 'name',
  code: 'code',
  vat: 'vat',
  sedeLegale: 'sedeLegaleAddress',
  notes: 'notes',
  reportRecipients: 'personnel',
  'alarmReference.contact': 'personnel',
  personnel: 'personnel',
};

const SITE_PATH_MAP: Record<string, string> = {
  name: 'name',
  address: 'sedeOperativaAddress',
  locationDescription: 'siteLocation',
  toxicGasLabel: 'toxicGas',
  'authorization.protocolNumber': 'authorizationProtocolNumber',
  'authorization.issueDate': 'authorizationIssueDate',
  'authorization.expiryDate': 'authorizationExpiryDate',
  'authorization.quantityKg': 'authorizedNh3Kg',
  'authorization.gazzetta.published': 'officialGazettePublished',
  'authorization.gazzetta.date': 'officialGazetteDate',
  'cpi.present': 'cpiPresent',
  'cpi.protocolNumber': 'cpiProtocolNumber',
  'cpi.lastRenewal': 'cpiLastRenewalDate',
  'cpi.expiry': 'cpiExpiryDate',
  'cpi.ammoniaActivityCodes': 'ammoniaActivityCodes',
  'schedule.frequencyLabel': 'frequency',
  'schedule.controlPeriod': 'periodicityControlPeriod',
  'schedule.previousVisit': 'periodicityPreviousVisit',
  'schedule.nextVisit': 'periodicityNextVisit',
  'schedule.nextVisitDisplay': 'periodicityNextVisit',
};

const SITE_EXTRA_FIELDS: Record<string, { field: FieldConfig; schema: ZodTypeAny }> = {
  siteLocation: {
    field: {
      key: 'siteLocation',
      label: 'Localizzazione',
      type: 'text',
      placeholder: 'Descrizione ubicazione stabilimento',
    },
    schema: optionalText(160),
  },
  toxicGas: {
    field: {
      key: 'toxicGas',
      label: 'Gas tossico',
      type: 'text',
      placeholder: 'Es: Ammoniaca Anidra',
    },
    schema: optionalText(120),
  },
  frequency: {
    field: {
      key: 'frequency',
      label: 'Frequenza sopralluoghi',
      type: 'select',
      options: [
        { value: 'monthly', label: 'Mensile' },
        { value: 'bimonthly', label: 'Bimestrale' },
        { value: 'quarterly', label: 'Trimestrale' },
        { value: 'four_monthly', label: 'Quadrimestrale' },
        { value: 'semiannual', label: 'Semestrale' },
        { value: 'annual', label: 'Annuale' },
      ],
    },
    schema: optionalText(40),
  },
  periodicityControlPeriod: {
    field: {
      key: 'periodicityControlPeriod',
      label: 'Periodo di controllo',
      type: 'text',
      placeholder: 'Es: GEN-MAR 2026',
    },
    schema: optionalText(40),
  },
  periodicityPreviousVisit: {
    field: {
      key: 'periodicityPreviousVisit',
      label: 'Ultimo sopralluogo (manuale)',
      type: 'date',
    },
    schema: optionalDate(),
  },
  periodicityNextVisit: {
    field: {
      key: 'periodicityNextVisit',
      label: 'Prossimo sopralluogo (manuale)',
      type: 'date',
    },
    schema: optionalDate(),
  },
};

const UNIT_FIELD_DEFINITIONS: Record<
  string,
  {
    key: string;
    field: FieldConfig;
    schema?: ZodTypeAny;
    updatePath?: string;
    componentType?: string;
    getDefault?: (unit: RefrigerationUnit) => unknown;
    normalizeValue?: (value: unknown) => unknown;
  }
> = {
  name: {
    key: 'name',
    field: {
      key: 'name',
      label: 'Nome impianto',
      type: 'text',
      required: true,
      placeholder: 'Es. Impianto produzione',
    },
    schema: requiredText(1, 120, {
      min: 'Inserisci il nome',
      max: 'Massimo 120 caratteri',
    }),
    updatePath: 'name',
  },
  location: {
    key: 'location',
    field: {
      key: 'location',
      label: 'Ubicazione',
      type: 'text',
      placeholder: 'Es. Sala macchine A',
    },
    updatePath: 'location',
    normalizeValue: (value) => ({
      kind: 'custom',
      text: normalizeFieldText(String(value ?? '')),
    }),
  },
  ammoniaKg: {
    key: 'ammoniaKg',
    field: {
      key: 'ammoniaKg',
      label: 'Ammoniaca (kg)',
      type: 'number',
      placeholder: 'Es. 4500',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'ammoniaKg',
  },
  'maintenanceContractor.name': {
    key: 'maintenanceName',
    field: {
      key: 'maintenanceName',
      label: 'Manutentore incaricato',
      type: 'text',
      placeholder: 'Es. Sud Refrigerazione',
    },
    updatePath: 'technical.maintenanceContractor.name',
  },
  'maintenanceContractor.notes': {
    key: 'maintenanceNotes',
    field: {
      key: 'maintenanceNotes',
      label: 'Note manutenzione',
      type: 'textarea',
      placeholder: 'Note aggiuntive...',
    },
    updatePath: 'technical.maintenanceContractor.notes',
  },
  // btGreaseBand is now per-component only, not at unit level
  'components.compressors': {
    key: 'componentsCompressors',
    field: {
      key: 'componentsCompressors',
      label: 'Compressori',
      type: 'number',
      placeholder: '0',
    },
    schema: optionalNumber(z.number().min(0)),
    componentType: 'compressore',
    getDefault: (unit) => sumComponentQuantity(unit, 'compressore'),
  },
  'components.oilSeparators': {
    key: 'componentsOilSeparators',
    field: {
      key: 'componentsOilSeparators',
      label: 'Separatori olio/NH3',
      type: 'number',
      placeholder: '0',
    },
    schema: optionalNumber(z.number().min(0)),
    componentType: 'separatore-olio-nh3',
    getDefault: (unit) => sumComponentQuantity(unit, 'separatore-olio-nh3'),
  },
  'components.lgSeparators': {
    key: 'componentsLgSeparators',
    field: {
      key: 'componentsLgSeparators',
      label: 'Separatori L/G',
      type: 'number',
      placeholder: '0',
    },
    schema: optionalNumber(z.number().min(0)),
    componentType: 'separatore-liquido-gas',
    getDefault: (unit) => sumComponentQuantity(unit, 'separatore-liquido-gas'),
  },
  'components.receivers': {
    key: 'componentsReceivers',
    field: {
      key: 'componentsReceivers',
      label: 'Ricevitori',
      type: 'number',
      placeholder: '0',
    },
    schema: optionalNumber(z.number().min(0)),
    componentType: 'ricevitore',
    getDefault: (unit) => sumComponentQuantity(unit, 'ricevitore'),
  },
  'components.condensers': {
    key: 'componentsCondensers',
    field: {
      key: 'componentsCondensers',
      label: 'Condensatori',
      type: 'number',
      placeholder: '0',
    },
    schema: optionalNumber(z.number().min(0)),
    componentType: 'condensatore',
    getDefault: (unit) => sumComponentQuantity(unit, 'condensatore'),
  },
  'components.exchangers': {
    key: 'componentsExchangers',
    field: {
      key: 'componentsExchangers',
      label: 'Scambiatori',
      type: 'number',
      placeholder: '0',
    },
    schema: optionalNumber(z.number().min(0)),
    componentType: 'scambiatore',
    getDefault: (unit) => sumComponentQuantity(unit, 'scambiatore'),
  },
  'components.nh3Pumps': {
    key: 'componentsNh3Pumps',
    field: {
      key: 'componentsNh3Pumps',
      label: 'Pompe NH3',
      type: 'number',
      placeholder: '0',
    },
    schema: optionalNumber(z.number().min(0)),
    componentType: 'pompa-nh3',
    getDefault: (unit) => sumComponentQuantity(unit, 'pompa-nh3'),
  },
  'components.externalEvaporators': {
    key: 'componentsExternalEvaporators',
    field: {
      key: 'componentsExternalEvaporators',
      label: 'Evaporatori esterni',
      type: 'number',
      placeholder: '0',
    },
    schema: optionalNumber(z.number().min(0)),
    componentType: 'evaporatore-esterno-sala-macchine',
    getDefault: (unit) => sumComponentQuantity(unit, 'evaporatore-esterno-sala-macchine'),
  },
  'safety.detectors.ceilingCount': {
    key: 'detectorsCeilingCount',
    field: {
      key: 'detectorsCeilingCount',
      label: 'Rilevatori quota soffitto',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.detectors.ceilingCount',
  },
  'safety.detectors.humanHeightCount': {
    key: 'detectorsHumanCount',
    field: {
      key: 'detectorsHumanCount',
      label: 'Rilevatori quota uomo',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.detectors.humanHeightCount',
  },
  'safety.detectors.positionsText': {
    key: 'detectorPositions',
    field: {
      key: 'detectorPositions',
      label: 'Posizioni rilevatori',
      type: 'textarea',
      placeholder: 'Es. Sala macchine, Locale evaporatori',
    },
    getDefault: (unit) => unit.technical?.safety?.detectors?.positions?.join(', ') ?? '',
    normalizeValue: (value) => splitList(String(value ?? '')),
    updatePath: 'technical.safety.detectors.positions',
  },
  'safety.emergencySystems.dialerPresent': {
    key: 'emergencyDialerPresent',
    field: {
      key: 'emergencyDialerPresent',
      label: 'Combinatore telefonico presente',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.emergencySystems.dialerPresent',
  },
  'safety.emergencySystems.guardServicePresent': {
    key: 'emergencyGuardServicePresent',
    field: {
      key: 'emergencyGuardServicePresent',
      label: 'Servizio di guardia presente',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.emergencySystems.guardServicePresent',
  },
  'safety.emergencySystems.generatorPresent': {
    key: 'emergencyGeneratorPresent',
    field: {
      key: 'emergencyGeneratorPresent',
      label: 'Generatore presente',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.emergencySystems.generatorPresent',
  },
  'safety.ventilation.naturalPresent': {
    key: 'ventilationNaturalPresent',
    field: {
      key: 'ventilationNaturalPresent',
      label: 'Ventilazione naturale presente',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.ventilation.naturalPresent',
  },
  'safety.ventilation.apertureHighM2': {
    key: 'ventilationNaturalHighM2',
    field: {
      key: 'ventilationNaturalHighM2',
      label: 'Aperture alte (m2)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.ventilation.apertureHighM2',
  },
  'safety.ventilation.apertureLowM2': {
    key: 'ventilationNaturalLowM2',
    field: {
      key: 'ventilationNaturalLowM2',
      label: 'Aperture basse (m2)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.ventilation.apertureLowM2',
  },
  'safety.ventilation.extractorsCount': {
    key: 'ventilationExtractUnits',
    field: {
      key: 'ventilationExtractUnits',
      label: 'Estrattori (n°)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.ventilation.extractorsCount',
  },
  'safety.ventilation.extractorsFlowM3h': {
    key: 'ventilationExtractFlowM3h',
    field: {
      key: 'ventilationExtractFlowM3h',
      label: 'Portata estrattori (m3/h)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.ventilation.extractorsFlowM3h',
  },
  'safety.ventilation.extractorsOperation': {
    key: 'ventilationExtractOperation',
    field: {
      key: 'ventilationExtractOperation',
      label: 'Logica estrattori',
      type: 'select',
      options: EXTRACT_OPERATION_OPTIONS,
    },
    updatePath: 'technical.safety.ventilation.extractorsOperation',
  },
  'safety.ventilation.extractorsAtex': {
    key: 'ventilationExtractAtex',
    field: {
      key: 'ventilationExtractAtex',
      label: 'Estrattori ATEX',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.ventilation.extractorsAtex',
  },
  'safety.scrubber.present': {
    key: 'scrubberPresent',
    field: {
      key: 'scrubberPresent',
      label: 'Scrubber presente',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.scrubber.present',
  },
  'safety.scrubber.airFlowM3h': {
    key: 'scrubberAirFlowM3h',
    field: {
      key: 'scrubberAirFlowM3h',
      label: 'Portata aria scrubber (m3/h)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.scrubber.airFlowM3h',
  },
  'safety.scrubber.waterFlowSpec': {
    key: 'scrubberWaterFlowSpec',
    field: {
      key: 'scrubberWaterFlowSpec',
      label: 'Portata acqua scrubber',
      type: 'text',
      placeholder: 'Es. 3 m3/h',
    },
    updatePath: 'technical.safety.scrubber.waterFlowSpec',
  },
  'safety.scrubber.contactTimeS': {
    key: 'scrubberContactTimeSeconds',
    field: {
      key: 'scrubberContactTimeSeconds',
      label: 'Tempo contatto scrubber (s)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.scrubber.contactTimeSeconds',
  },
  'safety.scrubber.atex': {
    key: 'scrubberAtex',
    field: {
      key: 'scrubberAtex',
      label: 'Scrubber certificato ATEX',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.scrubber.atex',
  },
  'safety.scrubber.waterSupply': {
    key: 'scrubberWaterSupply',
    field: {
      key: 'scrubberWaterSupply',
      label: 'Alimentazione idrica',
      type: 'text',
    },
    updatePath: 'technical.safety.scrubber.waterSupply',
  },
  'safety.scrubber.waterCollection': {
    key: 'scrubberWasteCollection',
    field: {
      key: 'scrubberWasteCollection',
      label: 'Raccolta acque',
      type: 'text',
    },
    updatePath: 'technical.safety.scrubber.wasteCollection',
  },
  'safety.vsRelief.collectorsCount': {
    key: 'vsReliefCollectorsCount',
    field: {
      key: 'vsReliefCollectorsCount',
      label: 'Collettori VS (n°)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.vsRelief.collectorsCount',
  },
  'safety.vsRelief.sizing': {
    key: 'vsReliefSizing',
    field: {
      key: 'vsReliefSizing',
      label: 'Dimensionamento VS',
      type: 'text',
    },
    updatePath: 'technical.safety.vsRelief.sizing',
  },
  'safety.abatementTank.volumeMc': {
    key: 'abatementVolumeMc',
    field: {
      key: 'abatementVolumeMc',
      label: 'Volume vasca (m3)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.abatementTank.volumeMc',
  },
  'safety.abatementTank.minWaterMc': {
    key: 'abatementMinWaterMc',
    field: {
      key: 'abatementMinWaterMc',
      label: 'Acqua minima (m3)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.abatementTank.minWaterMc',
  },
  'safety.abatementTank.minFreeMc': {
    key: 'abatementMinFreeMc',
    field: {
      key: 'abatementMinFreeMc',
      label: 'Volume libero (m3)',
      type: 'number',
    },
    schema: optionalNumber(z.number().min(0)),
    updatePath: 'technical.safety.abatementTank.minFreeMc',
  },
  'safety.abatementTank.location': {
    key: 'abatementLocation',
    field: {
      key: 'abatementLocation',
      label: 'Ubicazione vasca',
      type: 'select',
      options: LOCATION_OPTIONS,
    },
    updatePath: 'technical.safety.abatementTank.location',
  },
  'safety.abatementTank.signage': {
    key: 'abatementSignage',
    field: {
      key: 'abatementSignage',
      label: 'Segnaletica vasca',
      type: 'select',
      options: SIGNAGE_OPTIONS,
    },
    updatePath: 'technical.safety.abatementTank.signage',
  },
  'safety.abatementTank.accessibility': {
    key: 'abatementAccessibility',
    field: {
      key: 'abatementAccessibility',
      label: 'Accessibilità vasca',
      type: 'select',
      options: ACCESSIBILITY_OPTIONS,
    },
    updatePath: 'technical.safety.abatementTank.accessibility',
  },
  'safety.abatementTank.material': {
    key: 'abatementMaterial',
    field: {
      key: 'abatementMaterial',
      label: 'Materiale vasca',
      type: 'select',
      options: MATERIAL_OPTIONS,
    },
    updatePath: 'technical.safety.abatementTank.material',
  },
  'safety.abatementTank.dimensions': {
    key: 'abatementDimensions',
    field: {
      key: 'abatementDimensions',
      label: 'Dimensioni vasca',
      type: 'text',
      placeholder: 'Es. 4m x 2m x 2m',
    },
    updatePath: 'technical.safety.abatementTank.dimensions',
  },
  'safety.abatementTank.orientation': {
    key: 'abatementOrientation',
    field: {
      key: 'abatementOrientation',
      label: 'Orientamento vasca',
      type: 'select',
      options: ORIENTATION_OPTIONS,
    },
    updatePath: 'technical.safety.abatementTank.orientation',
  },
  'safety.abatementTank.levelInstrumentation.present': {
    key: 'abatementLevelInstrumentationPresent',
    field: {
      key: 'abatementLevelInstrumentationPresent',
      label: 'Strumentazione livello presente',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.abatementTank.levelInstrumentation.present',
  },
  'safety.abatementTank.levelInstrumentation.type': {
    key: 'abatementLevelInstrumentationType',
    field: {
      key: 'abatementLevelInstrumentationType',
      label: 'Tipo strumentazione livello',
      type: 'text',
      placeholder: 'Es. Indicatore elettronico',
    },
    updatePath: 'technical.safety.abatementTank.levelInstrumentation.type',
  },
  'safety.abatementTank.continuousPhMonitoring': {
    key: 'abatementContinuousPhMonitoring',
    field: {
      key: 'abatementContinuousPhMonitoring',
      label: 'Monitoraggio pH continuo',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.abatementTank.continuousPhMonitoring',
  },
  'safety.abatementTank.emergencySupply.preferredLine': {
    key: 'abatementEmergencyPreferredLine',
    field: {
      key: 'abatementEmergencyPreferredLine',
      label: 'Linea preferenziale',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.abatementTank.emergencySupply.preferredLine',
  },
  'safety.abatementTank.emergencySupply.generator': {
    key: 'abatementEmergencyGenerator',
    field: {
      key: 'abatementEmergencyGenerator',
      label: 'Generatore emergenza',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.abatementTank.emergencySupply.generator',
  },
  'safety.abatementTank.emergencySupply.ups': {
    key: 'abatementEmergencyUps',
    field: {
      key: 'abatementEmergencyUps',
      label: 'UPS emergenza',
      type: 'checkbox',
    },
    schema: z.boolean(),
    updatePath: 'technical.safety.abatementTank.emergencySupply.ups',
  },
  'safety.vvfDocs': {
    key: 'vvfDocs',
    field: {
      key: 'vvfDocs',
      label: 'Documentazione VVF',
      type: 'select',
      options: VVF_DOCS_OPTIONS,
    },
    updatePath: 'technical.safety.vvfDocs',
  },
  'safety.vvfDocsLocation': {
    key: 'vvfDocsLocation',
    field: {
      key: 'vvfDocsLocation',
      label: 'Collocazione documenti VVF',
      type: 'text',
    },
    updatePath: 'technical.safety.vvfDocsLocation',
  },
};

type EntityFieldTarget = {
  entity: SupportedEntity;
  key: string;
  sourcePath: string;
  field: FieldConfig;
  updateKey?: string;
  updatePath?: string;
  componentType?: string;
  componentIndex?: number;
  componentProperty?: 'quantity' | 'capacityKw' | 'location' | 'notes' | 'customLabel';
  schema?: ZodTypeAny;
  getDefault?: (entity: CompanyWithSites | Site | RefrigerationUnit) => unknown;
  normalizeValue?: (value: unknown) => unknown;
};

export type FocusedEntityResolution =
  | {
      type: 'focused';
      entity: SupportedEntity;
      targets: EntityFieldTarget[];
      schema: ZodTypeAny;
      defaultValues: Record<string, unknown>;
    }
  | { type: 'personnel' }
  | { type: 'fallback'; reason?: string };

const COMPANY_FIELDS_BY_KEY = new Map<string, FieldConfig>(
  companyFormFields.map((field) => [field.key, field])
);

const SITE_FIELDS_BY_KEY = new Map<string, FieldConfig>(
  siteFormFields.map((field) => [field.key, field])
);

function cloneField(field: FieldConfig): FieldConfig {
  return { ...field };
}

function resolveCompanyTargets(
  descriptor: EntityFieldDescriptor,
  company: CompanyWithSites
): FocusedEntityResolution {
  const paths = descriptor.source.paths.length ? descriptor.source.paths : [descriptor.source.path];

  if (paths.every((path) => COMPANY_PERSONNEL_PATHS.has(path))) {
    return { type: 'personnel' };
  }

  const targets: EntityFieldTarget[] = [];
  for (const path of paths) {
    const key = COMPANY_PATH_MAP[path];
    if (!key || key === 'personnel') {
      return { type: 'fallback', reason: 'unsupported-company-path' };
    }
    const baseField = COMPANY_FIELDS_BY_KEY.get(key);
    if (!baseField) {
      return { type: 'fallback', reason: 'missing-company-field' };
    }
    targets.push({
      entity: 'company',
      key,
      sourcePath: path,
      updateKey: key,
      field: cloneField(baseField),
    });
  }

  const uniqueTargets = dedupeTargets(targets);
  const labeledTargets = applyLabelOverride(uniqueTargets, descriptor.label);
  const schema = buildSchemaFromTargets(labeledTargets, companyFormSchema, {});
  const defaultValues = buildDefaultValues(labeledTargets, company);

  return {
    type: 'focused',
    entity: 'company',
    targets: labeledTargets,
    schema,
    defaultValues,
  };
}

function resolveSiteTargets(
  descriptor: EntityFieldDescriptor,
  site: Site
): FocusedEntityResolution {
  const paths = descriptor.source.paths.length ? descriptor.source.paths : [descriptor.source.path];

  const targets: EntityFieldTarget[] = [];
  for (const path of paths) {
    const key = SITE_PATH_MAP[path];
    if (!key) {
      return { type: 'fallback', reason: 'unsupported-site-path' };
    }

    const extra = SITE_EXTRA_FIELDS[key];
    if (extra) {
      targets.push({
        entity: 'site',
        key,
        sourcePath: path,
        updateKey: key,
        field: cloneField(extra.field),
        schema: extra.schema,
      });
      continue;
    }

    const baseField = SITE_FIELDS_BY_KEY.get(key);
    if (!baseField) {
      return { type: 'fallback', reason: 'missing-site-field' };
    }

    targets.push({
      entity: 'site',
      key,
      sourcePath: path,
      updateKey: key,
      field: cloneField(baseField),
    });
  }

  const uniqueTargets = dedupeTargets(targets);
  const labeledTargets = applyLabelOverride(uniqueTargets, descriptor.label);
  const schema = buildSchemaFromTargets(
    labeledTargets,
    siteFormSchema,
    Object.fromEntries(Object.entries(SITE_EXTRA_FIELDS).map(([key, value]) => [key, value.schema]))
  );
  const defaultValues = buildDefaultValues(labeledTargets, site);

  return {
    type: 'focused',
    entity: 'site',
    targets: labeledTargets,
    schema,
    defaultValues,
  };
}

function resolveUnitTargets(
  descriptor: EntityFieldDescriptor,
  unit: RefrigerationUnit | null
): FocusedEntityResolution {
  if (!unit) {
    return { type: 'fallback', reason: 'missing-unit' };
  }

  const paths = descriptor.source.paths.length ? descriptor.source.paths : [descriptor.source.path];

  const targets: EntityFieldTarget[] = [];
  for (const path of paths) {
    const componentMatch = matchComponentIndexPath(path);
    if (componentMatch) {
      const { index, property } = componentMatch;
      if (!unit.technical?.components?.[index]) {
        return { type: 'fallback', reason: 'component-index-out-of-range' };
      }
      targets.push({
        entity: 'unit',
        key: `components_${index}_${property}`,
        sourcePath: path,
        componentIndex: index,
        componentProperty: property,
        field: buildComponentIndexField(property, index),
      });
      continue;
    }

    const definition = UNIT_FIELD_DEFINITIONS[path];
    if (!definition) {
      return { type: 'fallback', reason: 'unsupported-unit-path' };
    }

    targets.push({
      entity: 'unit',
      key: definition.key,
      sourcePath: path,
      updatePath: definition.updatePath,
      componentType: definition.componentType,
      field: cloneField(definition.field),
      schema: definition.schema,
      getDefault: definition.getDefault
        ? (entity) => definition.getDefault?.(entity as RefrigerationUnit)
        : undefined,
      normalizeValue: definition.normalizeValue,
    });
  }

  const uniqueTargets = dedupeTargets(targets);
  const labeledTargets = applyLabelOverride(uniqueTargets, descriptor.label);
  const schema = buildSchemaFromTargets(labeledTargets, null, {});
  const defaultValues = buildDefaultValues(labeledTargets, unit);

  return {
    type: 'focused',
    entity: 'unit',
    targets: labeledTargets,
    schema,
    defaultValues,
  };
}

export function resolveEntityFieldTargets({
  descriptor,
  company,
  site,
  unit,
}: {
  descriptor: EntityFieldDescriptor;
  company: CompanyWithSites | null;
  site: Site | null;
  unit: RefrigerationUnit | null;
}): FocusedEntityResolution {
  switch (descriptor.source.entity) {
    case 'company':
      if (!company) {
        return { type: 'fallback', reason: 'missing-company' };
      }
      return resolveCompanyTargets(descriptor, company);
    case 'site':
      if (!site) {
        return { type: 'fallback', reason: 'missing-site' };
      }
      return resolveSiteTargets(descriptor, site);
    case 'unit':
      return resolveUnitTargets(descriptor, unit);
    case 'personnel':
      return { type: 'personnel' };
    default:
      return { type: 'fallback', reason: 'unsupported-entity' };
  }
}

export function buildCompanyUpdatePayload({
  targets,
  values,
  company,
}: {
  targets: EntityFieldTarget[];
  values: Record<string, unknown>;
  company: CompanyWithSites;
}): CompanyWithSites {
  const updates: Partial<CompanyWithSites> = {};

  targets
    .filter((target) => target.entity === 'company')
    .forEach((target) => {
      const key = target.updateKey ?? target.key;
      updates[key as keyof CompanyWithSites] = values[target.key] as never;
    });

  return {
    ...company,
    ...updates,
    createdAt: company.createdAt,
  };
}

export function buildSiteUpdatePayload({
  targets,
  values,
}: {
  targets: EntityFieldTarget[];
  values: Record<string, unknown>;
}): Record<string, unknown> {
  const updates: Record<string, unknown> = {};
  targets
    .filter((target) => target.entity === 'site')
    .forEach((target) => {
      const key = target.updateKey ?? target.key;
      updates[key] = values[target.key];
    });
  return updates;
}

export function buildUnitUpdatePayload({
  targets,
  values,
  unit,
}: {
  targets: EntityFieldTarget[];
  values: Record<string, unknown>;
  unit: RefrigerationUnit;
}): Record<string, unknown> {
  const update: Record<string, unknown> = { id: unit.id };
  let nextComponents = unit.technical?.components ? [...unit.technical.components] : [];
  let componentsTouched = false;

  for (const target of targets) {
    if (target.entity !== 'unit') continue;
    const rawValue = values[target.key];
    if (rawValue === undefined) continue;

    if (target.componentType) {
      nextComponents = updateComponentsByType(nextComponents, target.componentType, rawValue);
      componentsTouched = true;
      continue;
    }

    if (typeof target.componentIndex === 'number' && target.componentProperty) {
      nextComponents = updateComponentAtIndex(
        nextComponents,
        target.componentIndex,
        target.componentProperty,
        rawValue
      );
      componentsTouched = true;
      continue;
    }

    if (target.updatePath) {
      const value = target.normalizeValue ? target.normalizeValue(rawValue) : rawValue;
      mergeDeep(update, buildNestedUpdate(target.updatePath, value));
    }
  }

  if (componentsTouched) {
    mergeDeep(update, buildNestedUpdate('technical.components', nextComponents));
  }

  return update;
}

export function getEntityDisplayInfo({
  entity,
  company,
  site,
  unit,
}: {
  entity: SupportedEntity;
  company?: CompanyWithSites | null;
  site?: Site | null;
  unit?: RefrigerationUnit | null;
}): { title: string; context: string } {
  const companyName = company?.name?.trim() ?? '';
  const siteName = site?.name?.trim() ?? site?.address?.trim() ?? site?.siteLocation?.trim() ?? '';
  const unitName = unit?.name?.trim() ?? unit?.refrigerationUnitId ?? '';

  const joinPath = (parts: string[]) =>
    parts.filter((part) => part && part.trim().length > 0).join(' - ');

  switch (entity) {
    case 'company': {
      const name = companyName;
      return {
        title: name ? `Modifica Azienda: ${name}` : 'Modifica Azienda',
        context: name ? `Stai modificando l'azienda: ${name}` : "Stai modificando l'azienda",
      };
    }
    case 'site': {
      const name = siteName;
      const qualified = joinPath([companyName, siteName]);
      return {
        title: name ? `Modifica Stabilimento: ${name}` : 'Modifica Stabilimento',
        context: qualified
          ? `Stai modificando lo stabilimento: ${qualified}`
          : 'Stai modificando lo stabilimento',
      };
    }
    case 'unit': {
      const name = unitName;
      const qualified = joinPath([companyName, siteName, unitName]);
      return {
        title: name ? `Modifica Impianto: ${name}` : 'Modifica Impianto',
        context: qualified
          ? `Stai modificando l'impianto: ${qualified}`
          : "Stai modificando l'impianto",
      };
    }
    case 'personnel':
      return {
        title: 'Modifica Personale',
        context: companyName
          ? `Stai modificando il personale: ${companyName}`
          : 'Stai modificando il personale',
      };
    default:
      return { title: 'Modifica', context: '' };
  }
}

function dedupeTargets(targets: EntityFieldTarget[]): EntityFieldTarget[] {
  const seen = new Set<string>();
  return targets.filter((target) => {
    if (seen.has(target.key)) return false;
    seen.add(target.key);
    return true;
  });
}

function applyLabelOverride(
  targets: EntityFieldTarget[],
  labelOverride?: string
): EntityFieldTarget[] {
  if (!labelOverride || targets.length !== 1) return targets;
  const [target] = targets;
  return [
    {
      ...target,
      field: {
        ...target.field,
        label: labelOverride,
      },
    },
  ];
}

function buildSchemaFromTargets(
  targets: EntityFieldTarget[],
  baseSchema: z.ZodObject<Record<string, ZodTypeAny>> | null,
  extraSchemas: Record<string, ZodTypeAny>
): ZodTypeAny {
  const shape: Record<string, ZodTypeAny> = {};
  const baseShape = baseSchema?.shape ?? {};

  targets.forEach((target) => {
    const schema =
      target.schema ??
      baseShape[target.key] ??
      extraSchemas[target.key] ??
      inferSchemaFromField(target.field);
    shape[target.key] = schema;
  });

  return z.object(shape);
}

function inferSchemaFromField(field: FieldConfig): ZodTypeAny {
  switch (field.type) {
    case 'checkbox':
      return z.boolean();
    case 'number':
      return optionalNumber(z.number());
    case 'date':
      return optionalDate();
    default:
      return optionalText(320);
  }
}

function buildDefaultValues(
  targets: EntityFieldTarget[],
  entity?: CompanyWithSites | Site | RefrigerationUnit
): Record<string, unknown> {
  const defaultValues: Record<string, unknown> = {};
  const resolvedEntity = entity ?? null;

  targets.forEach((target) => {
    let rawValue: unknown = undefined;
    if (target.getDefault && resolvedEntity) {
      rawValue = target.getDefault(resolvedEntity);
    } else if (resolvedEntity && (target.updateKey || target.updatePath)) {
      const path = target.updateKey ?? target.updatePath ?? '';
      rawValue = getValueAtPath(resolvedEntity, path);
    }

    defaultValues[target.key] = normalizeDefaultValue(rawValue, target.field);
  });

  return defaultValues;
}

function normalizeDefaultValue(value: unknown, field: FieldConfig): unknown {
  if (field.type === 'checkbox') {
    return Boolean(value);
  }
  if (field.type === 'date') {
    return formatItDateInputValue(value);
  }
  if ((field.type === 'text' || field.type === 'textarea') && value) {
    const fieldValue = value as {
      kind?: string;
      optionId?: string;
      text?: string;
    };
    if (fieldValue.kind === 'option') {
      return fieldValue.optionId ?? '';
    }
    if (fieldValue.kind === 'custom' || fieldValue.kind === 'text') {
      return fieldValue.text ?? '';
    }
  }
  if (value === undefined || value === null) return '';
  return value;
}

function getValueAtPath(obj: unknown, path: string): unknown {
  if (!obj || !path) return undefined;
  const segments = path.split('.');
  let current: unknown = obj;
  for (const segment of segments) {
    const match = segment.match(/^(\w+)\[(\d+)\]$/);
    if (match) {
      const [, key, rawIndex] = match;
      const index = Number(rawIndex);
      if (
        typeof current === 'object' &&
        current !== null &&
        key in (current as Record<string, unknown>)
      ) {
        const value = (current as Record<string, unknown>)[key];
        if (Array.isArray(value)) {
          current = value[index];
          continue;
        }
      }
      return undefined;
    }

    if (
      typeof current === 'object' &&
      current !== null &&
      segment in (current as Record<string, unknown>)
    ) {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return current;
}

function buildNestedUpdate(path: string, value: unknown): Record<string, unknown> {
  const segments = path.split('.');
  const root: Record<string, unknown> = {};
  let cursor: Record<string, unknown> = root;

  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      cursor[segment] = value;
      return;
    }
    if (!cursor[segment] || typeof cursor[segment] !== 'object') {
      cursor[segment] = {};
    }
    cursor = cursor[segment] as Record<string, unknown>;
  });

  return root;
}

function mergeDeep(
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> {
  for (const [key, value] of Object.entries(source)) {
    const existing = target[key];
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      existing &&
      typeof existing === 'object' &&
      !Array.isArray(existing)
    ) {
      target[key] = mergeDeep(
        existing as Record<string, unknown>,
        value as Record<string, unknown>
      );
    } else {
      target[key] = value;
    }
  }
  return target;
}

function sumComponentQuantity(unit: RefrigerationUnit, type: string): number {
  const components = unit.technical?.components ?? [];
  if (!components.length) return 0;
  return components
    .filter((component) => component.type === type)
    .reduce((sum, component) => sum + (component.quantity ?? 0), 0);
}

function updateComponentsByType(
  components: RefrigerationUnit['technical']['components'],
  type: string,
  rawValue: unknown
): RefrigerationUnit['technical']['components'] {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return components;
  const quantity = Math.max(0, Math.round(parsed));

  const remaining = components.filter((component) => component.type !== type);
  if (quantity <= 0) {
    return remaining;
  }

  const existing = components.find((component) => component.type === type);
  const nextComponent = existing
    ? { ...existing, quantity }
    : {
        id: newId(),
        type,
        customLabel: undefined,
        quantity,
        location: undefined,
        notes: undefined,
      };

  return [...remaining, nextComponent];
}

function updateComponentAtIndex(
  components: RefrigerationUnit['technical']['components'],
  index: number,
  property: 'quantity' | 'capacityKw' | 'location' | 'notes' | 'customLabel',
  value: unknown
): RefrigerationUnit['technical']['components'] {
  if (!components[index]) return components;
  const next = [...components];
  const current = next[index];
  if (!current) return components;

  const nextValue =
    property === 'quantity'
      ? Math.max(0, Number(value) || 0)
      : property === 'capacityKw'
        ? Number.isFinite(Number(value))
          ? Math.max(0, Number(value))
          : undefined
        : value;

  next[index] = {
    ...current,
    [property]: nextValue,
  };

  return next;
}

function matchComponentIndexPath(path: string): {
  index: number;
  property: 'quantity' | 'capacityKw' | 'location' | 'notes' | 'customLabel';
} | null {
  const match = path.match(/^(?:technical\.)?components\[(\d+)\]\.(\w+)$/);
  if (!match) return null;
  const index = Number(match[1]);
  const property = match[2];
  if (!Number.isFinite(index)) return null;
  if (
    property !== 'quantity' &&
    property !== 'capacityKw' &&
    property !== 'location' &&
    property !== 'notes' &&
    property !== 'customLabel'
  ) {
    return null;
  }
  return {
    index,
    property: property as 'quantity' | 'location' | 'notes' | 'customLabel',
  };
}

function buildComponentIndexField(
  property: 'quantity' | 'capacityKw' | 'location' | 'notes' | 'customLabel',
  index: number
): FieldConfig {
  const baseLabel =
    property === 'quantity'
      ? 'Quantità componente'
      : property === 'capacityKw'
        ? 'Potenza/Capacità componente (kW)'
        : property === 'location'
          ? 'Ubicazione componente'
          : property === 'notes'
            ? 'Note componente'
            : 'Descrizione componente';

  const type = property === 'quantity' || property === 'capacityKw' ? 'number' : 'text';

  return {
    key: `components_${index}_${property}`,
    label: `${baseLabel} ${index + 1}`,
    type,
  };
}

function splitList(value: string): string[] {
  return value
    .split(/[;\n,]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}
